import React, { useEffect, useState } from "react";
import { Button, Col, Row, Stack } from "react-bootstrap";
import SelectObject from "../../ui/SelectObject";
import SelectObjectTypeahead from "../../ui/SelectObjectTypeahead";
import Input from "../../ui/Input";
import { fetchResourceTypes, postResource } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";

interface AddResourceState {
  setOrgToSave: any;
  disableType: boolean;
  type: string;
  category: string | null;
  setShowAddResource: any;
  setShowAddConsumable: any;
  setShowAddNonConsumable: any;
  getResources: any;
  resourceCategories: any[];
  getResourceCategories: any;
  onAddResourceCategory: any;
  setErrMsg: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const AddResource = ({
  setOrgToSave,
  disableType,
  type,
  category,
  setShowAddResource,
  setShowAddConsumable,
  setShowAddNonConsumable,
  getResources,
  resourceCategories,
  getResourceCategories,
  onAddResourceCategory,
  setErrMsg,
  setShowToast,
  setToastText,
  setToastBody,
}: AddResourceState) => {
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [allowSaveResource, setAllowSaveResource] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<string>(type);
  const [resourceCategory, setResourceCategory] = useState<any>({});
  const [resourceName, setResourceName] = useState<string>("");
  const [resourceQuantity, setResourceQuantity] = useState<number>();

  useEffect(() => {
    const validInput = resourceType !== "0" && resourceName?.trim() && resourceQuantity;

    validInput ? setAllowSaveResource(true) : setAllowSaveResource(false);
  }, [resourceType, resourceName, resourceQuantity]);

  useEffect(() => {
    async function fetchData() {
      const response = await Promise.all([getResourceTypes(), getResourceCategories()]);
      if (response) {
        if (category) setResourceCategory(resourceCategories.find((r: any) => r.key === category));
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (resourceCategory?.id && resourceCategories.length > 0) {
      const updatedCategory = resourceCategories.find((c: any) => c.id === resourceCategory.id);
      if (updatedCategory && updatedCategory !== resourceCategory) {
        setResourceCategory(updatedCategory);
      }
    }
  }, [resourceCategories]);

  const getResourceTypes = () => {
    fetchResourceTypes()
      .then((res: any) => {
        const types = res[0].data;
        setResourceTypes(types);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setShowAddConsumable(false);
    setShowAddNonConsumable(false);
    setResourceType("0");
    setResourceCategory({});
    setResourceName("");
    setResourceQuantity(undefined);
    setOrgToSave(false);
    getResources();
  };

  const onClickSaveResource = (isDone: boolean = false) => {
    setErrMsg("");

    const newResource = {
      type_key: resourceType,
      category_key: resourceCategory?.key,
      name: resourceName,
      quantity: resourceQuantity,
    };

    postResource(newResource)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Created");
        setToastBody(resourceName);
        setResourceCategory({});
        setResourceName("");
        setResourceQuantity(undefined);
        unsavedChanges.value = false;
        setAllowSaveResource(false);
        setOrgToSave(false);
        if (isDone) {
          setShowAddResource(false);
          setShowAddConsumable(false);
          setShowAddNonConsumable(false);
          setResourceType("0");
        }
        getResources();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const updateName = (e: any) => {
    setResourceName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateQuantity = (e: any) => {
    setResourceQuantity(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  return (
    <Stack gap={3} className="my-3">
      <Row>
        <Col xs={6} lg={4}>
          <SelectObject
            label="Type"
            value={resourceType}
            options={resourceTypes}
            onUpdate={(e: any) => setResourceType(e.target.value)}
            required={true}
            disabled={disableType}
            hasFocus={disableType ? false : true}
          />
        </Col>
        <Col xs={6} lg={4}>
          <SelectObjectTypeahead
            initialOption="Search or type to create"
            label="Category"
            value={resourceCategory}
            options={resourceCategories}
            onUpdate={(e: any) => {
              setResourceCategory(e);
            }}
            focused={disableType ? true : false}
            allowCreate={true}
            onCreateOption={async (inputValue: string) => {
              const newCategory = await onAddResourceCategory(inputValue);
              setResourceCategory(newCategory);
              return newCategory;
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={9} lg={6}>
          <Input label="Name" type="text" value={resourceName} onUpdate={(e: any) => updateName(e)} required={true} />
        </Col>
        <Col xs={3} lg={2}>
          <Input label="Quantity" type="number" value={resourceQuantity} onUpdate={(e: any) => updateQuantity(e)} required={true} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={8}>
          <Stack direction="horizontal" gap={3}>
            <Button className="app-secondary-color button" onClick={() => onClickSaveResource(false)} disabled={!allowSaveResource}>
              Save and Add Another
            </Button>
            <div className="vr" />
            <Button className="app-secondary-color button" onClick={() => onClickSaveResource(true)} disabled={!allowSaveResource}>
              Save and Done
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickAddResourceCancel()}>
              Done
            </Button>
          </Stack>
        </Col>
      </Row>
    </Stack>
  );
};

export default AddResource;
